import { ROLE_TYPE_ID_TO_STRING } from '@sportninja/common/constants/app'

import { getAnOrA } from '../../../../pages/InviteV2'
import type { InviteSignupInvite } from '../types'

export { getAnOrA }

/** Invitation type ids that change what the screen renders. */
const PLAYER_INVITE_TYPE_ID = '1'
const VIDEOGRAPHER_INVITE_TYPE_ID = '11'

/**
 * Only player invitations collect a date of birth.
 *
 * Kept as a helper because the legacy component derived this in two places
 * (render and submit) and the two must not drift apart.
 */
export const getIsPlayerInvite = (
  inviteTypeId: InviteSignupInvite['inviteTypeId']
): boolean => String(inviteTypeId) === PLAYER_INVITE_TYPE_ID

/** Videographer invites name the destination "Game" and the role "Videographer". */
export const getIsVideographerInvite = (
  inviteTypeId: InviteSignupInvite['inviteTypeId']
): boolean => String(inviteTypeId) === VIDEOGRAPHER_INVITE_TYPE_ID

/**
 * The pieces the context line above the form is built from.
 *
 * The legacy screen derived these inline in `render()`; pulling them out keeps
 * the component to rendering and makes the branches testable.
 */
export type InviteContext = {
  /** Invitee's name as the invite was issued — not the editable form value. */
  inviteeName: string
  /** Org / team / competition / game the invite is for. */
  destination: string
  /** Role label, e.g. "Admin". Empty when the invite carries no role. */
  role: string
  /** "a" or "an", agreeing with `role`. */
  roleArticle: string
}

/**
 * Build the context line's parts from the invitation.
 *
 * Mirrors the legacy derivation exactly, including the `replace(/$the[/s]/i)`
 * call — that regex never matches (`$` anchors the end), but removing it would
 * be a behavior change, so it is preserved verbatim.
 */
export const getInviteContext = (invite: InviteSignupInvite): InviteContext => {
  const {
    inviterName,
    roleTypeId,
    type,
    inviteTypeId,
    origNameFirst,
    origNameLast,
  } = invite

  const inviteeName = [origNameFirst, origNameLast].filter(Boolean).join(' ')

  const removeTheFromName = (inviterName || '').replace(/$the[/s]/i, '')
  const roleName = roleTypeId
    ? (ROLE_TYPE_ID_TO_STRING as Record<string, string>)?.[String(roleTypeId)]
    : undefined

  const isVideographer = getIsVideographerInvite(inviteTypeId)
  const destination = isVideographer ? 'Game' : removeTheFromName

  const role = isVideographer
    ? 'Videographer'
    : type !== 'player' && roleName
    ? roleName
    : type

  return {
    inviteeName,
    destination,
    role: role || '',
    roleArticle: getAnOrA(role),
  }
}

/**
 * Normalize a date of birth to the `YYYY-MM-DD` that `DateOfBirthInput` expects.
 *
 * The invite returns `birth_date` as a full ISO datetime
 * (e.g. "2023-03-03T00:00:00+00:00"). Matches the logged-in invite screen's
 * `normalizeBirthDate`, including returning '' for missing/short values, so the
 * two screens read the same field the same way.
 */
export const normalizeBirthDate = (value?: string | false): string =>
  typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : ''
